import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";
import { Loader2 } from "lucide-react";
import { TimeoutType } from '../hooks/useSessionTimeout';

interface SessionWarningModalProps {
    isOpen: boolean;
    timeoutType: TimeoutType;
    timeLeft: number;
    onStaySignedIn: () => void;
    onSignOut: () => void;
    stayLoading?: boolean;
    signOutLoading?: boolean;
}

export const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
    isOpen,
    timeoutType,
    timeLeft,
    onStaySignedIn,
    onSignOut,
    stayLoading = false,
    signOutLoading = false,
}) => {
    const { t } = useTranslation('auth');
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (timeoutType === TimeoutType.NONE) return null;

    const isAbsolute = timeoutType === TimeoutType.ABSOLUTE;

    return (
        <AlertDialog open={isOpen}>
            <AlertDialogContent ariaComplete className="w-[calc(100%-1rem)] sm:max-w-sm gap-0 p-4 sm:p-6 shadow-2xl ring-2 ring-gray-200/80 bg-white">
                <div className="text-center mb-4 sm:mb-6">
                    <AlertDialogTitle className="text-base sm:text-lg font-semibold text-gray-900">
                        {isAbsolute ? t('session.sessionExpiring') : t('session.sessionTimeout')}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-xs sm:text-sm text-gray-500 mt-1">
                        {isAbsolute
                            ? t('session.maxDurationReached')
                            : t('session.inactiveDetected')}
                    </AlertDialogDescription>
                </div>

                {/* Timer - sidebar navy (#203D5C) for brand consistency with app sidebar */}
                <div className="flex justify-center mb-4 sm:mb-6">
                    <div
                        className={`text-4xl sm:text-5xl font-semibold tracking-tight tabular-nums ${
                            timeLeft <= 10
                                ? 'text-[var(--sidebar-accent)]'
                                : 'text-[var(--sidebar)]'
                        }`}
                    >
                        {formatTime(timeLeft)}
                    </div>
                </div>

                {/* Message */}
                <p className="text-sm text-gray-600 text-center mb-4 sm:mb-6">
                    {isAbsolute
                        ? t('session.cannotExtend')
                        : t('session.clickToStaySignedIn')}
                </p>

                {/* Buttons - muted, professional */}
                <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-3">
                    <Button
                        variant="outline"
                        onClick={onSignOut}
                        disabled={signOutLoading || stayLoading}
                        className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                    >
                        {signOutLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            t('session.signOut')
                        )}
                    </Button>

                    {!isAbsolute && (
                        <Button
                            variant="outline"
                            onClick={onStaySignedIn}
                            disabled={stayLoading || signOutLoading}
                            className="w-full border-primary/40 text-primary hover:bg-primary/5"
                        >
                            {stayLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                t('session.continueSession')
                            )}
                        </Button>
                    )}
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
